import React, { useEffect, useMemo, useState } from "react";
import { Global } from "../helpers/Global";
import * as XLSX from "xlsx";

// ✅ Toast global
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { DataGrid } from "@mui/x-data-grid";

// Icons (MUI)
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import MapIcon from "@mui/icons-material/Map";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

// ===== Leaflet / React-Leaflet =====
import { MapContainer, TileLayer, Marker, LayersControl, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fingerprint
import { FingerprintCheckerJornadas } from "./fingerprintJornadas";

// Fix icon paths (webpack)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// Custom markers
const edificioIcon = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const cierreIcon = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png",
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

export const JornadaCentros = () => {
  const toast = useToast();

  const [jornadas, setJornadas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [centros, setCentros] = useState([]);

  const [filtroUsuario, setFiltroUsuario] = useState("");
  const [filtroCentro, setFiltroCentro] = useState("");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");

  // DataGrid paginación
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // ===== Modal edición =====
  const [modalEditar, setModalEditar] = useState(false);
  const [jornadaEditar, setJornadaEditar] = useState(null);

  const [editInicio, setEditInicio] = useState("");
  const [editFin, setEditFin] = useState("");
  const [editMotivo, setEditMotivo] = useState("");
  const [editObs, setEditObs] = useState("");
  const [editError, setEditError] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  // ===== Modal mapa =====
  const [mostrarMapa, setMostrarMapa] = useState(false);
  const [mapFocus, setMapFocus] = useState("inicio"); // "inicio" | "cierre"
  const [ubicacionInicio, setUbicacionInicio] = useState(null);
  const [ubicacionCierre, setUbicacionCierre] = useState(null);
  const [ubicacionCentro, setUbicacionCentro] = useState(null);

  // --------------------------
  // Helpers
  // --------------------------
  const parseDateSafe = (value) => {
    if (!value) return null;
    const normalized =
      typeof value === "string" && value.includes(" ") && !value.includes("T")
        ? value.replace(" ", "T")
        : value;

    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const fmtDate = (value) => {
    const d = parseDateSafe(value);
    if (!d) return "-";
    return d.toLocaleString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  // YMD local (evita mordidas por UTC)
  const toLocalYMD = (v) => {
    const d = parseDateSafe(v);
    if (!d) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const toDatetimeLocalString = (dateValue) => {
    const d = parseDateSafe(dateValue);
    if (!d) return "";
    const pad = (n) => String(n).padStart(2, "0");
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const min = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  };

  const calcularDuracion = (ms) => {
    if (!ms || isNaN(ms)) return "No disponible";
    const horas = Math.floor(ms / 3600000);
    const minutos = Math.floor((ms % 3600000) / 60000);
    return `${horas}h ${minutos}min`;
  };

  const nombreUsuario = (id) => {
    const u = usuarios.find((x) => String(x.id) === String(id));
    return u ? `${u.nombre} ${u.apellidos || ""}`.trim() : "-";
  };

  const nombreValidador = (id) => {
    if (!id) return "-";
    const u = usuarios.find((x) => String(x.id) === String(id));
    return u ? `${u.nombre} ${u.apellidos || ""}`.trim() : `ID ${id}`;
  };

  const fmtBoolSN = (b) => (b ? "Sí" : "No");

  // --------------------------
  // Carga
  // --------------------------
  useEffect(() => {
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarDatos = async () => {
    try {
      const [resUsuarios, resCentros, resJornadas] = await Promise.all([
        fetch(`${Global.url}usuarios`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
        fetch(`${Global.url}centros`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
        // ⚠️ Asumimos backend filtrando por autorización.
        fetch(`${Global.url}jornadas/finalizadas`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
      ]);

      const usuariosData = resUsuarios.ok ? await resUsuarios.json() : [];
      const centrosData = resCentros.ok ? await resCentros.json() : [];
      const jornadasData = resJornadas.ok ? await resJornadas.json() : [];

      const usuariosById = new Map(usuariosData.map((u) => [String(u.id), u]));
      const centrosById = new Map(centrosData.map((c) => [String(c.id), c]));

      const enriquecidas = (jornadasData || []).map((j) => {
        const creador = usuariosById.get(String(j.creado_por_admin_id));
        const centroObj = centrosById.get(String(j.centro_id));

        const ini = parseDateSafe(j.inicio)?.getTime() ?? 0;
        const fin = parseDateSafe(j.fin)?.getTime() ?? 0;

        return {
          ...j,
          duracionMs: fin - ini,
          creado_por_nombre: creador ? `${creador.nombre} ${creador.apellidos || ""}`.trim() : "-",
          centro: centroObj ? centroObj.nombre : `Centro ${j.centro_id}`,
        };
      });

      setUsuarios(usuariosData);
      setCentros(centrosData);
      setJornadas(enriquecidas);
    } catch (err) {
      console.error("Error al cargar datos:", err);
      toast.error("Error al cargar los datos iniciales.");
    }
  };

  // --------------------------
  // Centros permitidos (por jornadas devueltas)
  // --------------------------
  const allowedCentroIds = useMemo(() => {
    const s = new Set();
    (jornadas || []).forEach((j) => s.add(String(j.centro_id)));
    return s;
  }, [jornadas]);

  const centrosPermitidosOrdenados = useMemo(() => {
    return (centros || [])
      .filter((c) => allowedCentroIds.has(String(c.id)))
      .slice()
      .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""));
  }, [centros, allowedCentroIds]);

  const centroSeleccionado =
    centrosPermitidosOrdenados.find((c) => String(c.id) === String(filtroCentro)) || null;

  const usuariosOrdenados = useMemo(() => {
    return (usuarios || [])
      .slice()
      .sort((a, b) =>
        (a.nombre + (a.apellidos || "")).localeCompare(b.nombre + (b.apellidos || ""))
      );
  }, [usuarios]);

  const usuarioSeleccionado =
    usuariosOrdenados.find((u) => String(u.id) === String(filtroUsuario)) || null;

  // --------------------------
  // Filtros
  // --------------------------
  const limpiarFiltros = () => {
    setFiltroUsuario("");
    setFiltroCentro("");
    setFechaDesde("");
    setFechaHasta("");
    toast.info("Filtros limpiados.");
  };

  const jornadasFiltradas = useMemo(() => {
    return (jornadas || []).filter((j) => {
      const inicioISO = toLocalYMD(j.inicio);

      const matchUsuario = !filtroUsuario || String(j.usuario_id) === String(filtroUsuario);
      const matchCentro = !filtroCentro || String(j.centro_id) === String(filtroCentro);

      const matchFecha =
        !fechaDesde && !fechaHasta
          ? true
          : fechaDesde && !fechaHasta
          ? inicioISO === fechaDesde
          : fechaDesde && fechaHasta
          ? inicioISO >= fechaDesde && inicioISO <= fechaHasta
          : true;

      return matchUsuario && matchCentro && matchFecha;
    });
  }, [jornadas, filtroUsuario, filtroCentro, fechaDesde, fechaHasta]);

  useEffect(() => setPage(0), [filtroUsuario, filtroCentro, fechaDesde, fechaHasta, pageSize]);

  // --------------------------
  // Validación
  // --------------------------
  const toggleValidada = async (j) => {
    try {
      const res = await fetch(`${Global.url}jornadas/${j.id}/validar`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ validada: !j.validada }),
      });

      if (!res.ok) {
        toast.error(
          res.status === 403
            ? "No tienes permisos para validar esta jornada."
            : "No se pudo cambiar el estado de validación."
        );
        return;
      }

      const updated = await res.json();
      const ini = parseDateSafe(updated.inicio)?.getTime() ?? 0;
      const fin = parseDateSafe(updated.fin)?.getTime() ?? 0;

      setJornadas((prev) =>
        prev.map((row) => (row.id === j.id ? { ...row, ...updated, duracionMs: fin - ini } : row))
      );

      toast.success(updated.validada ? "Jornada validada." : "Validación retirada.");
    } catch (e) {
      console.error(e);
      toast.error("Error de red al validar/desvalidar la jornada.");
    }
  };

  // --------------------------
  // Modal edición
  // --------------------------
  useEffect(() => {
    if (!modalEditar || !jornadaEditar) return;
    setEditInicio(toDatetimeLocalString(jornadaEditar.inicio));
    setEditFin(toDatetimeLocalString(jornadaEditar.fin));
    setEditMotivo(jornadaEditar.motivo_fin || "");
    setEditObs(jornadaEditar.observaciones_admin || "");
    setEditError("");
  }, [modalEditar, jornadaEditar]);

  const handleGuardarEdicion = async () => {
    if (!jornadaEditar) return;

    if (!editInicio || !editFin) {
      setEditError("Debes indicar inicio y fin.");
      return;
    }
    if (!editObs.trim()) {
      setEditError("Debes introducir una observación.");
      return;
    }

    const dInicio = new Date(editInicio);
    const dFin = new Date(editFin);
    if (Number.isNaN(dInicio.getTime()) || Number.isNaN(dFin.getTime()) || dFin < dInicio) {
      setEditError("La fecha de fin debe ser posterior al inicio.");
      return;
    }

    setSavingEdit(true);
    try {
      const res = await fetch(`${Global.url}jornadas/modificar/${jornadaEditar.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          usuario_id: jornadaEditar.usuario_id,
          centro_id: jornadaEditar.centro_id,
          inicio: editInicio,
          fin: editFin,
          motivo_fin: editMotivo,
          observaciones_admin: editObs,
        }),
      });

      if (!res.ok) {
        toast.error("Error al modificar la jornada.");
        return;
      }

      toast.success("Jornada modificada correctamente.");
      setModalEditar(false);
      setJornadaEditar(null);
      await cargarDatos();
    } catch (e) {
      console.error(e);
      toast.error("Error al conectar con el servidor.");
    } finally {
      setSavingEdit(false);
    }
  };

  // --------------------------
  // Mapa
  // --------------------------
  const abrirMapa = async (j, focus = "inicio") => {
    setMapFocus(focus);

    const ini = j.latitud && j.longitud ? { lat: Number(j.latitud), lng: Number(j.longitud) } : null;
    const fin =
      j.latitud_cierre && j.longitud_cierre
        ? { lat: Number(j.latitud_cierre), lng: Number(j.longitud_cierre) }
        : null;

    setUbicacionInicio(ini);
    setUbicacionCierre(fin);

    try {
      const res = await fetch(`${Global.url}centros/${j.centro_id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const data = await res.json();

      if (res.ok && data?.latitud && data?.longitud) {
        setUbicacionCentro({ lat: parseFloat(data.latitud), lng: parseFloat(data.longitud) });
      } else {
        setUbicacionCentro(null);
      }
    } catch (err) {
      console.error("Error al obtener centro:", err);
      setUbicacionCentro(null);
    }

    setMostrarMapa(true);
  };

  const mapCenter = useMemo(() => {
    const pick =
      mapFocus === "cierre"
        ? ubicacionCierre || ubicacionInicio
        : ubicacionInicio || ubicacionCierre;
    return pick ? [pick.lat, pick.lng] : [0, 0];
  }, [mapFocus, ubicacionInicio, ubicacionCierre]);

  // --------------------------
  // Export CSV / Excel
  // --------------------------
  const exportarCSVReducido = (datos, nombreArchivo = "jornadas.csv") => {
    if (!Array.isArray(datos) || datos.length === 0) {
      toast.warning("No hay datos para exportar.");
      return;
    }

    const encabezados = [
      "Usuario",
      "Centro",
      "Inicio",
      "Fin",
      "Duración",
      "En Rango (Inicio)",
      "En Rango (Cierre)",
      "Distancia (Inicio m)",
      "Distancia (Cierre m)",
      "Validada",
      "Validado por",
      "Fecha validación",
      "Motivo Fin",
      "Creado por",
      "Observaciones",
    ];

    const csvRows = [
      encabezados.join(","),
      ...datos.map((j) => {
        const fila = [
          nombreUsuario(j.usuario_id),
          j.centro ?? "",
          j.inicio ? fmtDate(j.inicio) : "-",
          j.fin ? fmtDate(j.fin) : "-",
          calcularDuracion(j.duracionMs),
          j.en_rango ? "En rango" : "Fuera de rango",
          j.en_rango_cierre == null ? "-" : j.en_rango_cierre ? "En rango" : "Fuera de rango",
          j.distancia_m != null ? j.distancia_m : "",
          j.distancia_m_cierre != null ? j.distancia_m_cierre : "",
          fmtBoolSN(!!j.validada),
          j.validada ? nombreValidador(j.validada_por) : "-",
          j.validada ? fmtDate(j.validada_en) : "-",
          j.motivo_fin ?? "-",
          j.creado_por_nombre || "-",
          j.observaciones_admin || "-",
        ];
        return fila.map((val) => `"${val?.toString().replace(/"/g, '""') ?? ""}"`).join(",");
      }),
    ];

    const csvContent = "data:text/csv;charset=utf-8,\uFEFF" + csvRows.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", nombreArchivo);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(`CSV generado: ${nombreArchivo}`);
  };

  const exportarExcel = (datos, nombreArchivo = "jornadas.xlsx") => {
    if (!Array.isArray(datos) || datos.length === 0) {
      toast.warning("No hay datos para exportar.");
      return;
    }

    const datosFormatoExcel = datos.map((j) => ({
      Usuario: nombreUsuario(j.usuario_id),
      Centro: j.centro ?? "",
      Inicio: j.inicio ? fmtDate(j.inicio) : "-",
      Fin: j.fin ? fmtDate(j.fin) : "-",
      Duración: calcularDuracion(j.duracionMs),
      "En Rango (Inicio)": j.en_rango ? "En rango" : "Fuera de rango",
      "En Rango (Cierre)":
        j.en_rango_cierre == null ? "-" : j.en_rango_cierre ? "En rango" : "Fuera de rango",
      "Distancia (Inicio m)": j.distancia_m != null ? j.distancia_m : "",
      "Distancia (Cierre m)": j.distancia_m_cierre != null ? j.distancia_m_cierre : "",
      Validada: fmtBoolSN(!!j.validada),
      "Validado por": j.validada ? nombreValidador(j.validada_por) : "-",
      "Fecha validación": j.validada ? fmtDate(j.validada_en) : "-",
      "Motivo Fin": j.motivo_fin ?? "-",
      "Creado por": j.creado_por_nombre || "-",
      Observaciones: j.observaciones_admin || "-",
    }));

    const hoja = XLSX.utils.json_to_sheet(datosFormatoExcel);
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, "Jornadas");
    XLSX.writeFile(libro, nombreArchivo);

    toast.success(`Excel generado: ${nombreArchivo}`);
  };

  // --------------------------
  // DataGrid rows + columns (SIN columna En rango)
  // --------------------------
  const rows = useMemo(() => {
    return (jornadasFiltradas || []).map((j) => ({
      ...j,
      id: j.id,
      usuario_nombre: nombreUsuario(j.usuario_id),
      duracion_txt: calcularDuracion(j.duracionMs),
      validador_txt: j.validada ? nombreValidador(j.validada_por) : "-",
      validada_en_txt: j.validada ? fmtDate(j.validada_en) : "-",
    }));
  }, [jornadasFiltradas, usuarios]);

  const columns = useMemo(
    () => [
      { field: "usuario_nombre", headerName: "Usuario", flex: 1, minWidth: 220 },
      { field: "centro", headerName: "Centro", flex: 1, minWidth: 220 },
      { field: "inicio", headerName: "Inicio", minWidth: 170, renderCell: (p) => fmtDate(p.row.inicio) },
      { field: "fin", headerName: "Fin", minWidth: 170, renderCell: (p) => fmtDate(p.row.fin) },
      { field: "duracion_txt", headerName: "Duración", minWidth: 120 },

      {
        field: "distancias",
        headerName: "Distancias",
        minWidth: 340,
        sortable: false,
        renderCell: (params) => {
          const j = params.row;

          const inicioOk = j.en_rango === true;
          const cierreOk = j.en_rango_cierre === true;
          const cierreUnknown = j.en_rango_cierre == null;

          const chipColorInicio = inicioOk ? "success" : "error";
          const chipColorCierre = cierreUnknown ? "default" : cierreOk ? "success" : "error";

          return (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%", width: "100%", overflow: "hidden" }}>
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  flexWrap: "nowrap",
                  whiteSpace: "nowrap",
                  overflowX: "auto",
                  overflowY: "hidden",
                  width: "100%",
                  pb: 0.25,
                  "&::-webkit-scrollbar": { height: 6 },
                }}
              >
                {j.distancia_m != null ? (
                  <Chip
                    label={`Inicio: ${j.distancia_m} m`}
                    color={chipColorInicio}
                    variant="filled"
                    clickable
                    onClick={() => abrirMapa(j, "inicio")}
                  />
                ) : (
                  <Chip label="Inicio: -" color="default" variant="filled" />
                )}

                {j.distancia_m_cierre != null ? (
                  <Chip
                    label={`Cierre: ${j.distancia_m_cierre} m`}
                    color={chipColorCierre}
                    variant="filled"
                    clickable
                    onClick={() => abrirMapa(j, "cierre")}
                  />
                ) : (
                  <Chip label="Cierre: -" color="default" variant="filled" />
                )}
              </Stack>
            </Box>
          );
        },
      },

      {
        field: "validada",
        headerName: "Validada",
        minWidth: 130,
        renderCell: (params) =>
          params.row.validada ? (
            <Chip icon={<CheckCircleIcon />} label="Sí" color="success" variant="filled" />
          ) : (
            <Chip icon={<CancelIcon />} label="No" color="default" variant="filled" />
          ),
      },

      {
        field: "acciones",
        headerName: "Acciones",
        minWidth: 270,
        sortable: false,
        renderCell: (params) => {
          const j = params.row;
          return (
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Button
                size="small"
                startIcon={<EditIcon />}
                onClick={() => {
                  setJornadaEditar(j);
                  setModalEditar(true);
                }}
              >
                Editar
              </Button>

              <Button size="small" onClick={() => toggleValidada(j)}>
                {j.validada ? "Quitar" : "Validar"}
              </Button>
            </Stack>
          );
        },
      },

      { field: "validador_txt", headerName: "Validado por", minWidth: 220 },
      { field: "validada_en_txt", headerName: "Validada en", minWidth: 180 },
      { field: "motivo_fin", headerName: "Motivo fin", minWidth: 180, flex: 1 },
      { field: "creado_por_nombre", headerName: "Creado/Modificado por", minWidth: 200 },
      { field: "observaciones_admin", headerName: "Observaciones", minWidth: 260, flex: 1 },
    ],
    [usuarios]
  );

  return (
    <Box className="appContainer appContainer--wide">
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Jornadas por centros
        </Typography>

        {/* Filtros */}
        <Paper sx={{ p: 2 }}>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                md: "2fr 2fr 1fr 1fr auto",
              },
              alignItems: "end",
            }}
          >
            <Autocomplete
              size="small"
              options={usuariosOrdenados}
              getOptionLabel={(u) => `${u.nombre} ${u.apellidos || ""}`.trim()}
              isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
              value={usuarioSeleccionado}
              onChange={(_, newValue) => setFiltroUsuario(newValue ? newValue.id : "")}
              renderInput={(params) => (
                <TextField {...params} label="Usuario" placeholder="Escribe para buscar…" />
              )}
              clearOnEscape
            />

            <Autocomplete
              size="small"
              options={centrosPermitidosOrdenados}
              getOptionLabel={(c) => c?.nombre || ""}
              isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
              value={centroSeleccionado}
              onChange={(_, newValue) => setFiltroCentro(newValue ? newValue.id : "")}
              renderInput={(params) => (
                <TextField {...params} label="Centro" placeholder="Escribe para buscar…" />
              )}
              clearOnEscape
            />

            <TextField
              label="Desde"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
            />

            <TextField
              label="Hasta"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
            />

            <Button onClick={limpiarFiltros} sx={{ height: 40 }}>
              Limpiar
            </Button>
          </Box>
        </Paper>

        {/* Tabla */}
        <Paper sx={{ height: 660, p: 1 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            disableRowSelectionOnClick
            pagination
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(m) => {
              setPage(m.page);
              setPageSize(m.pageSize);
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            sx={{
              "& .MuiDataGrid-columnHeaders": { borderBottom: "1px solid rgba(0,0,0,0.08)" },
              "& .MuiDataGrid-cell": { borderBottom: "1px solid rgba(0,0,0,0.06)" },
            }}
          />
        </Paper>

        {/* Exportaciones */}
        <Paper sx={{ p: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} useFlexGap flexWrap="wrap">
            <Button startIcon={<DownloadIcon />} onClick={() => exportarCSVReducido(jornadas, "todas_jornadas.csv")}>
              CSV Todas
            </Button>
            <Button
              startIcon={<DownloadIcon />}
              onClick={() => exportarCSVReducido(jornadasFiltradas, "jornadas_filtradas.csv")}
            >
              CSV Filtradas
            </Button>
            <Button startIcon={<DownloadIcon />} onClick={() => exportarExcel(jornadas, "todas_jornadas.xlsx")}>
              Excel Todas
            </Button>
            <Button
              startIcon={<DownloadIcon />}
              onClick={() => exportarExcel(jornadasFiltradas, "jornadas_filtradas.xlsx")}
            >
              Excel Filtradas
            </Button>
          </Stack>
        </Paper>

        <FingerprintCheckerJornadas />

        {/* MODAL EDICIÓN */}
        <Dialog
          open={modalEditar}
          onClose={() => {
            if (savingEdit) return;
            setModalEditar(false);
            setJornadaEditar(null);
          }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Modificar jornada</DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2}>
              <TextField
                label="Inicio"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={editInicio}
                onChange={(e) => setEditInicio(e.target.value)}
              />

              <TextField
                label="Fin"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={editFin}
                onChange={(e) => setEditFin(e.target.value)}
              />

              <TextField
                label="Motivo fin"
                value={editMotivo}
                onChange={(e) => setEditMotivo(e.target.value)}
                placeholder="Motivo de la modificación"
              />

              <TextField
                label="Observaciones"
                value={editObs}
                onChange={(e) => setEditObs(e.target.value)}
                placeholder="Observaciones administrativas"
                multiline
                minRows={3}
              />

              {editError && <Typography color="error">{editError}</Typography>}
            </Stack>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setModalEditar(false);
                setJornadaEditar(null);
              }}
              disabled={savingEdit}
            >
              Cancelar
            </Button>
            <Button onClick={handleGuardarEdicion} disabled={savingEdit}>
              {savingEdit ? "Guardando..." : "Guardar"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* MODAL MAPA */}
        <Dialog open={mostrarMapa} onClose={() => setMostrarMapa(false)} fullWidth maxWidth="md">
          <DialogTitle>Ubicación de la jornada</DialogTitle>
          <Divider />
          <DialogContent sx={{ p: 2 }}>
            {ubicacionInicio || ubicacionCierre ? (
              <Box sx={{ width: "100%", height: 460 }}>
                <MapContainer center={mapCenter} zoom={15} style={{ height: "100%", width: "100%" }}>
                  <LayersControl position="topright">
                    <LayersControl.BaseLayer checked name="Satélite + Callejero">
                      <TileLayer
                        url={`https://api.maptiler.com/maps/hybrid/256/{z}/{x}/{y}.jpg?key=${process.env.REACT_APP_MAPTILER_KEY}&language=es`}
                        attribution='&copy; <a href="https://www.maptiler.com/copyright/">MapTiler</a>'
                      />
                    </LayersControl.BaseLayer>
                  </LayersControl>

                  {ubicacionInicio && (
                    <Marker position={[ubicacionInicio.lat, ubicacionInicio.lng]}>
                      <Tooltip direction="top" offset={[0, -10]}>
                        📍 Jornada (inicio)
                      </Tooltip>
                    </Marker>
                  )}

                  {ubicacionCierre && (
                    <Marker position={[ubicacionCierre.lat, ubicacionCierre.lng]} icon={cierreIcon}>
                      <Tooltip direction="top" offset={[0, -10]}>
                        ✅ Jornada (cierre)
                      </Tooltip>
                    </Marker>
                  )}

                  {ubicacionCentro && (
                    <Marker position={[ubicacionCentro.lat, ubicacionCentro.lng]} icon={edificioIcon}>
                      <Tooltip direction="top" offset={[0, -10]}>
                        🏢 Centro de trabajo
                      </Tooltip>
                    </Marker>
                  )}
                </MapContainer>
              </Box>
            ) : (
              <Typography>No hay coordenadas para mostrar.</Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant="outlined" onClick={() => setMostrarMapa(false)}>
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Box>
  );
};
